import { useCallback, useEffect, useMemo, useState } from 'react'
import type { Contato, ContatoPapel } from '@services/db.js'
import {
  addContato,
  deleteContato,
  getContatos,
  updateContato,
  type NovoContato,
} from '@services/gestao.js'
import { CONTATO_PAPEIS } from '@utils/constants.js'

export const PAPEL_LABEL: Record<string, string> = Object.fromEntries(
  CONTATO_PAPEIS.map((p) => [p.value, p.label]),
)

export interface ContatoItem extends Contato {
  id: number
  papelLabel: string
}

export interface FiltrosContatos {
  busca: string
  papel: '' | ContatoPapel
}

/** Só dígitos, com DDI 55 quando ausente — formato aceito pelo wa.me. */
export function telefoneParaWhatsApp(telefone: string): string | null {
  const digits = telefone.replace(/\D/g, '')
  if (digits.length < 10) return null
  return digits.startsWith('55') && digits.length >= 12 ? digits : `55${digits}`
}

export function formatTelefone(telefone: string): string {
  const d = telefone.replace(/\D/g, '')
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`
  if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`
  return telefone
}

export function useContatos() {
  const [todos, setTodos] = useState<ContatoItem[]>([])
  const [filtros, setFiltros] = useState<FiltrosContatos>({ busca: '', papel: '' })
  const [isLoading, setIsLoading] = useState(true)

  const load = useCallback(async () => {
    const raw = await getContatos()
    setTodos(
      raw.map((c) => ({ ...c, id: c.id as number, papelLabel: PAPEL_LABEL[c.papel] ?? 'Outro' })),
    )
    setIsLoading(false)
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const itens = useMemo(() => {
    const q = filtros.busca.trim().toLowerCase()
    return todos.filter((c) => {
      if (filtros.papel && c.papel !== filtros.papel) return false
      if (!q) return true
      return [c.nome, c.empresa, c.papelLabel, c.telefone, c.observacoes]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(q)
    })
  }, [todos, filtros])

  const papeisPresentes = useMemo(() => {
    const set = new Set(todos.map((c) => c.papel))
    return CONTATO_PAPEIS.filter((p) => set.has(p.value as ContatoPapel))
  }, [todos])

  const criar = useCallback(
    async (input: NovoContato) => {
      await addContato(input)
      await load()
    },
    [load],
  )

  const editar = useCallback(
    async (id: number, input: Partial<NovoContato>) => {
      await updateContato(id, input)
      await load()
    },
    [load],
  )

  const remover = useCallback(async (id: number) => {
    await deleteContato(id)
    setTodos((prev) => prev.filter((c) => c.id !== id))
  }, [])

  return { itens, todos, filtros, setFiltros, papeisPresentes, isLoading, criar, editar, remover }
}
